<template>
  <section class="ba-section">
    <div class="ba-header">
      <div>
        <h4 class="ba-title">Cuentas bancarias</h4>
        <p class="ba-subtitle">
          Administra las cuentas bancarias de tu negocio
        </p>
      </div>
      <button v-if="insert" class="ba-btn-add" @click="onClickAddBankAccount">
        <i class="el-icon-plus"></i>
        Nueva cuenta
      </button>
    </div>

    <SkeletonOneCardComponent height="80px" v-if="toggleApiRequest" />

    <template v-else>
      <div v-if="bankAccounts.length" class="ba-list">
        <div
          v-for="(bankAccount, index) in bankAccounts"
          :key="index"
          class="ba-card"
        >
          <div class="ba-card-icon">
            <i class="el-icon-office-building"></i>
          </div>
          <div class="ba-card-info">
            <span class="ba-card-name">{{ bankAccount.name }}</span>
            <span class="ba-card-detail" v-if="bankAccount.account_number">
              {{ bankAccount.type || "Cuenta" }} &middot;
              {{ bankAccount.account_number }}
            </span>
            <span class="ba-card-detail" v-if="bankAccount.customer_name">
              {{ bankAccount.customer_name }}
            </span>
          </div>
          <div class="ba-card-actions">
            <button
              v-if="edit"
              class="ba-action-btn"
              @click="onClickEditBankAccount(bankAccount, index)"
              title="Editar"
            >
              <i class="el-icon-edit"></i>
            </button>
            <ButtonDeleteComponent
              :isShowButton="false"
              @onConfirmDelete="onConfirmDeleteBankAccount(bankAccount, index)"
            />
          </div>
        </div>
      </div>
      <div v-else class="ba-empty">
        <div class="ba-empty-icon">
          <i class="el-icon-wallet"></i>
        </div>
        <p class="ba-empty-text">No tienes cuentas bancarias registradas</p>
        <button
          v-if="insert"
          class="ba-btn-add ba-btn-add--outline"
          @click="onClickAddBankAccount"
        >
          <i class="el-icon-plus"></i>
          Añadir cuenta
        </button>
      </div>
    </template>

    <WrapperDrawer
      :onVisible.sync="drawerBankAccountAdd"
      @onCloseDrawer="onCloseDrawerBankAccountAdd"
    >
      <BankAccountAdd @onBankAccountAdd="onBankAccountAdd" />
    </WrapperDrawer>

    <WrapperDrawer
      :onVisible.sync="drawerBankAccountEdit"
      @onCloseDrawer="onCloseDrawerBankAccountEdit"
    >
      <BankAccountEdit
        :bankAccount.sync="bankAccountEdit"
        @onBankAccountEdit="onBankAccountEdit"
      />
    </WrapperDrawer>
  </section>
</template>

<script>
import ButtonDeleteComponent from "@/modules/dashboard/components/ButtonDelete";
import WrapperDrawer from "@/components/General/WrapperDrawer";
import BankAccountAdd from "./BankAccountAdd";
import BankAccountEdit from "./BankAccountEdit";
import SkeletonOneCardComponent from "@/components/Animations/Loading/SkeletonOneCard";

import businessService from "../../services/index";

export default {
  name: "BankAccountList",
  data() {
    return {
      toggleApiRequest: false,
      bankAccounts: [],
      drawerBankAccountAdd: false,
      drawerBankAccountEdit: false,
      bankAccountEdit: null
    };
  },
  props: {
    edit: {
      default: true
    },
    insert: {
      default: true
    }
  },
  methods: {
    onClickAddBankAccount() {
      this.drawerBankAccountAdd = true;
    },
    onCloseDrawerBankAccountAdd() {
      this.drawerBankAccountAdd = false;
    },
    onCloseDrawerBankAccountEdit() {
      this.drawerBankAccountEdit = false;
    },
    onBankAccountAdd({ bankAccount }) {
      this.bankAccounts = [...[bankAccount], ...this.bankAccounts];
      this.onCloseDrawerBankAccountAdd();
    },
    onBankAccountEdit({ bankAccount }) {
      const bankAccountIndex = this.bankAccounts.find(
        (b, index) => b.id === bankAccount.id && index
      );
      this.bankAccounts[bankAccountIndex] = bankAccount;
    },
    onClickEditBankAccount(bankAccount) {
      this.bankAccountEdit = bankAccount;
      this.drawerBankAccountEdit = true;
    },
    async onConfirmDeleteBankAccount({ id: bankAccountId }, index) {
      this.$store.dispatch("toggleRequestLoading");
      try {
        let response = await businessService
          .deleteBankAccount(bankAccountId)
          .finally(() => {
            this.$store.dispatch("toggleRequestLoading");
          });
        const { success, message } = response.data;
        if (success) {
          this.bankAccounts.splice(index, 1);
          this.$notifications.success({
            message
          });
        }
      } catch (error) {
        return false;
      }
    },

    async getBankAccounts() {
      try {
        this.toggleApiRequest = true;
        let response = await businessService.getBankAccounts().finally(() => {
          this.toggleApiRequest = false;
        });
        const { data, success } = response.data;
        if (success) {
          this.bankAccounts = data;
        }
      } catch (error) {
        return false;
      }
    }
  },
  components: {
    ButtonDeleteComponent,
    WrapperDrawer,
    BankAccountAdd,
    BankAccountEdit,
    SkeletonOneCardComponent
  },
  mounted() {
    this.getBankAccounts();
  }
};
</script>

<style lang="scss" scoped>
$primary: #2d2b55;
$accent: #3ecfb4;
$green: #1d9e75;
$border: #e8ecf1;
$bg: #f7f8fa;

.ba-section {
  padding: 4px 0;
}

.ba-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
}

.ba-title {
  font-size: 18px;
  font-weight: 700;
  color: $primary;
  margin: 0;
}

.ba-subtitle {
  font-size: 13px;
  color: #909399;
  margin: 4px 0 0;
}

.ba-btn-add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 18px;
  background: $accent;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;

  &:hover {
    background: $green;
  }

  &--outline {
    background: transparent;
    color: $accent;
    border: 1.5px dashed $accent;

    &:hover {
      background: rgba($accent, 0.06);
      color: $green;
      border-color: $green;
    }
  }
}

.ba-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ba-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: #fff;
  border: 1.5px solid $border;
  border-radius: 12px;
  transition: all 0.15s;

  &:hover {
    border-color: darken($border, 8%);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  }
}

.ba-card-icon {
  width: 42px;
  height: 42px;
  min-width: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f0fdf9 0%, #e8f8f3 100%);
  border-radius: 10px;
  color: $green;
  font-size: 18px;
}

.ba-card-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.ba-card-name {
  font-size: 14px;
  font-weight: 600;
  color: $primary;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ba-card-detail {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ba-card-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.ba-action-btn {
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 8px;
  color: #909399;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.12s;

  &:hover {
    background: $bg;
    color: $primary;
  }
}

// Empty state
.ba-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  text-align: center;
}

.ba-empty-icon {
  width: 56px;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $bg;
  border-radius: 14px;
  color: #c0c4cc;
  font-size: 24px;
  margin-bottom: 14px;
}

.ba-empty-text {
  font-size: 14px;
  color: #909399;
  margin: 0 0 16px;
}
</style>
